import React from 'react';
import { motion } from 'framer-motion';

const LEVELS = [
  { id: 'all', name: 'Tous', color: '#3BADE5', bgColor: 'bg-[#3BADE5]/20', textColor: 'text-[#3BADE5]', icon: '🎯' },
  { id: 'debutant', name: 'Débutant', color: '#22c55e', bgColor: 'bg-green-500/20', textColor: 'text-green-500', icon: '🌱' },
  { id: 'intermediaire', name: 'Intermédiaire', color: '#eab308', bgColor: 'bg-yellow-500/20', textColor: 'text-yellow-500', icon: '⚡' },
  { id: 'avance', name: 'Avancé', color: '#ef4444', bgColor: 'bg-red-500/20', textColor: 'text-red-500', icon: '🔥' },
];

/**
 * LevelFilter - Compact floating filter for session levels
 * @param {string} selectedLevel - Currently selected level id
 * @param {function} onLevelChange - Callback when level changes
 * @param {number} counts - Object with counts per level { all: N, debutant: N, ... }
 * @param {string} variant - 'floating' | 'inline' | 'minimal'
 * @param {string} className - Additional classes
 */
export default function LevelFilter({ 
  selectedLevel = 'all', 
  onLevelChange, 
  counts = {},
  variant = 'floating',
  className = ''
}) {
  const baseClasses = {
    floating: 'absolute top-16 left-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-full shadow-lg border border-gray-200 p-1.5',
    inline: 'bg-card border border-border rounded-full p-1.5',
    minimal: 'bg-transparent p-0',
  };

  return (
    <div 
      className={`${baseClasses[variant]} ${className}`}
      data-testid="level-filter"
    >
      <div className="flex items-center gap-1">
        {LEVELS.map((level) => {
          const isSelected = selectedLevel === level.id;
          const count = counts[level.id] || 0;
          
          return (
            <motion.button
              key={level.id}
              onClick={() => onLevelChange(level.id)}
              className={`
                relative px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200
                flex items-center gap-1.5
                ${isSelected 
                  ? `${level.bgColor} ${level.textColor} ring-2 ring-offset-1`
                  : 'text-gray-500 hover:bg-gray-100'
                }
              `}
              style={isSelected ? { 
                ringColor: level.color,
                boxShadow: `0 0 0 2px ${level.color}25`
              } : {}}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid={`level-filter-${level.id}`}
            >
              <span className="text-sm">{level.icon}</span>
              <span className="hidden sm:inline">{level.name}</span>
              {counts && count > 0 && (
                <span 
                  className={`
                    ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold
                    ${isSelected ? 'bg-white/30' : 'bg-gray-200/50'}
                  `}
                >
                  {count}
                </span>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Compact inline level badge for use in lists or cards
 */
export function LevelBadge({ level, size = 'default' }) {
  const levelConfig = LEVELS.find(l => l.id === level) || LEVELS[0];
  
  const sizeClasses = {
    small: 'px-2 py-0.5 text-[10px]',
    default: 'px-2.5 py-1 text-xs',
    large: 'px-3 py-1.5 text-sm',
  };
  
  return (
    <span 
      className={`
        inline-flex items-center gap-1 rounded-full font-medium
        ${levelConfig.bgColor} ${levelConfig.textColor}
        ${sizeClasses[size]}
      `}
    >
      <span>{levelConfig.icon}</span>
      <span>{levelConfig.name}</span>
    </span>
  );
}

/**
 * Get counts of sessions per level
 */
export function getSessionCountsByLevel(sessions) {
  const counts = {
    all: sessions.length,
    debutant: 0,
    intermediaire: 0,
    avance: 0,
    tous: 0,
  };
  
  sessions.forEach(session => {
    if (counts[session.level] !== undefined) {
      counts[session.level]++;
    }
  });
  
  // "tous" level sessions count as accessible to all
  counts.all = sessions.length;
  
  return counts;
}

export { LEVELS };
